import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { useTranslation } from "@privacy/translations";
import { Button } from "@mui/material";
import * as FormDialog from "../shared/FormDialog";
import { EmailField, NameFields, type UserFormValues } from "../shared/UserFormFields";
import { hasErrorCode } from "../api/errors";
import {
  createProductAdminMutationOptions,
  PRODUCT_ADMIN_USER_ALREADY_MEMBER_OF_CUSTOMER,
  userEmailLookupMutationOptions,
  type EnterpriseCustomerOrganisation,
  type ProductAdminUser,
  type UpdateProductAdminsPayload,
} from "../api/enterpriseCustomer";

type Step =
  | { name: "email" }
  | { name: "details"; source: "customer" | "visma-connect"; user: ProductAdminUser }
  | { name: "details"; source: "none" };

const hasEmail = (email: string) => (user: ProductAdminUser) => user.email.toLowerCase() === email.toLowerCase();

export const AddProductAdminDialog = ({
  organisation,
  customerUsers,
  updateProductAdminsMutation,
  defaultEmail,
  onClose,
}: {
  organisation: EnterpriseCustomerOrganisation;
  customerUsers: ProductAdminUser[];
  updateProductAdminsMutation: UseMutationResult<unknown, Error, UpdateProductAdminsPayload>;
  defaultEmail: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>({ name: "email" });
  const lookupMutation = useMutation(userEmailLookupMutationOptions());
  const createProductAdminMutation = useMutation(createProductAdminMutationOptions());
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const {
    control,
    handleSubmit,
    trigger,
    getValues,
    setValue,
    setError,
    clearErrors,
    setFocus,
    formState: { errors },
  } = useForm<UserFormValues>({ defaultValues: { email: defaultEmail.trim(), firstName: "", lastName: "" } });

  useEffect(() => {
    const timeoutId = setTimeout(() => setFocus("email"));
    return () => clearTimeout(timeoutId);
  }, [setFocus]);

  const isDetails = step.name === "details";
  const namesLocked = step.name === "details" && step.source !== "none";

  const handleNext = async () => {
    if (!(await trigger("email"))) return;
    const email = getValues("email");
    if (organisation.productAdmins.some(hasEmail(email))) {
      setError("email", { message: t("EnterpriseCustomer.Organisation.ProductAdmins.Dialog.AlreadyAdmin") });
      return;
    }
    const member = customerUsers.find(hasEmail(email));
    const known = member ?? (await lookupMutation.mutateAsync(email).catch(() => null));
    setValue("firstName", known?.firstName ?? "");
    setValue("lastName", known?.lastName ?? "");
    if (member) setStep({ name: "details", source: "customer", user: member });
    else if (known) setStep({ name: "details", source: "visma-connect", user: known });
    else setStep({ name: "details", source: "none" });
  };

  const handleBack = () => {
    setStep({ name: "email" });
    clearErrors();
    setTimeout(() => setFocus("email"));
  };

  const handleAdd = handleSubmit(({ email, firstName, lastName }) => {
    if (step.name !== "details") return;
    const callbacks = {
      onSuccess: onClose,
      onError: (error: unknown) =>
        hasErrorCode(error, PRODUCT_ADMIN_USER_ALREADY_MEMBER_OF_CUSTOMER)
          ? setError("email", { message: t("EnterpriseCustomer.Organisation.ProductAdmins.Dialog.AlreadyMember") })
          : setError("root.serverError", { type: "fallback" }),
    };
    if (step.source === "customer") {
      const userIds = [...organisation.productAdmins.map(({ userId }) => userId), step.user.userId];
      updateProductAdminsMutation.mutate({ organisationId: organisation.id, userIds }, callbacks);
    } else {
      createProductAdminMutation.mutate({ organisationId: organisation.id, email, firstName, lastName }, callbacks);
    }
  });

  return (
    <FormDialog.Dialog isOpen>
      <FormDialog.Form onSubmit={event => void (isDetails ? handleAdd(event) : handleNext())}>
        <FormDialog.Title>{t("EnterpriseCustomer.Organisation.ProductAdmins.Dialog.Header")}</FormDialog.Title>
        <FormDialog.Content>
          <FormDialog.FormError isError={Boolean(errors.root?.serverError)} />
          <FormDialog.Fields>
            <EmailField control={control} disabled={isDetails} />
            <FormDialog.Reveal
              isOpen={isDetails}
              onRevealed={() => {
                if (namesLocked) addButtonRef.current?.focus();
                else setFocus("firstName");
              }}
            >
              <NameFields control={control} disabled={namesLocked} />
            </FormDialog.Reveal>
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          {isDetails ? (
            <>
              <Button variant="outlined" onClick={handleBack}>
                {t("EnterpriseCustomer.Organisation.ProductAdmins.Dialog.BackButton")}
              </Button>
              <FormDialog.SubmitButton
                ref={addButtonRef}
                isSubmitting={updateProductAdminsMutation.isPending || createProductAdminMutation.isPending}
              >
                {t("EnterpriseCustomer.Organisation.ProductAdmins.Dialog.SubmitButton")}
              </FormDialog.SubmitButton>
            </>
          ) : (
            <>
              <FormDialog.CancelButton onClick={onClose} />
              <FormDialog.SubmitButton isSubmitting={lookupMutation.isPending}>
                {t("EnterpriseCustomer.Organisation.ProductAdmins.Dialog.NextButton")}
              </FormDialog.SubmitButton>
            </>
          )}
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
